import * as React from "react";
import { Button, TextField } from "@material-ui/core";
import { useEffect, useState } from "react";
import { useSelector, useDispatch } from "react-redux";
import { useNavigate, useParams } from "react-router";
import { LocalizerHelper } from "../../helpers/localizerHelper";
import { checkIfNotEmpty } from "../../helpers/validatorHelper";
import Screen from "../screen";
import RestaurantHeader from "../restaurant/RestaurantHeader";
import { RootState } from "../../store";
import { DeliveryDetailsUpdate } from "../../models/session";
import { updateDeliveryInfo } from "../../features/session/session.service";
import './index.scss';
import { auth } from "../../firebase";

const InfoDetails: React.FC = () => {
    const navigate = useNavigate();

    const { restaurant: restaurantId, menu: menuId, link_alias, dits: sessionId, mode } = useParams<{ restaurant: string; menu: string, link_alias: string, dits: string, mode:string}>();

    const dits = useSelector((state: RootState) => state.session.session);

    const [deliveryDetails, setDeliveryDetails] = useState<DeliveryDetailsUpdate>({
        address: dits?.delivery_details?.address ?? "",
        doorbell_name: dits?.delivery_details?.doorbell_name ?? "",
        phone_number: dits?.delivery_details?.phone_number ?? "",
        notes: dits?.delivery_details?.notes ?? ""
    });

    const [errors, setErrors] = useState({
        address: !checkIfNotEmpty(deliveryDetails.address),
        doorbell_name: !checkIfNotEmpty(deliveryDetails.doorbell_name),
        phone_number: !checkIfNotEmpty(deliveryDetails.phone_number),
        notes: false
    });

    const [disableAction, setDisableAction] = useState(true);

    const linkAlias = link_alias ?? window.localStorage.getItem('chuzeat_last_alias_link');

    const fields = mode === "delivery" ? ["address", "doorbell_name", "phone_number", "notes"] as (keyof DeliveryDetailsUpdate)[]
        : ["phone_number", "notes"] as (keyof DeliveryDetailsUpdate)[];

    // Validate form whenever fields or mode change
    useEffect(() => {
        validateFields();
    }, [deliveryDetails, mode]);

    useEffect(() => {
        setDeliveryDetails({
            address: dits?.delivery_details?.address ?? "",
            doorbell_name: dits?.delivery_details?.doorbell_name ?? "",
            phone_number: dits?.delivery_details?.phone_number ?? "",
            notes: dits?.delivery_details?.notes ?? ""
        })
    }, [dits]);

    const handleChange = (field: keyof DeliveryDetailsUpdate, value: string) => {
        setDeliveryDetails(prev => ({ ...prev, [field]: value }));
    };

    const validateFields = () => {
        const newErrors = {
            address: !checkIfNotEmpty(deliveryDetails.address),
            doorbell_name: !checkIfNotEmpty(deliveryDetails.doorbell_name),
            phone_number: !checkIfNotEmpty(deliveryDetails.phone_number),
            notes: false,
        };
        setErrors(newErrors);

        if (mode === "delivery") setDisableAction(newErrors.address || newErrors.doorbell_name);
        if (mode === "takeaway") setDisableAction(newErrors.phone_number);
    };

    const proceedWithOrder = async () => {
        if (!dits || !restaurantId || !sessionId) return;

        await updateDeliveryInfo(restaurantId, sessionId, deliveryDetails);

        if (auth.currentUser?.isAnonymous) {
            navigate(`/menu/${linkAlias}/signin/${restaurantId}/${sessionId}/${menuId}`);
        } else {
            navigate(`/menu/${linkAlias}/confirm-order/${restaurantId}/${sessionId}/${menuId}`);
        }
    };

    const buildField = (field: keyof DeliveryDetailsUpdate, labelKey: string, multiline = false) => (
        <div className="row form-row" key={field}>
            <div className="col-md-12">
                <TextField
                    InputProps={{
                        readOnly: field === "address"
                    }}
                    variant="outlined"
                    name={field}
                    fullWidth
                    multiline={multiline}
                    minRows={multiline ? 4 : 1}
                    type={field === "phone_number" ? "tel" : "text"}
                    value={deliveryDetails[field]}
                    label={LocalizerHelper.localized(labelKey)}
                    error={errors[field] ?? false}
                    helperText={errors[field] ? LocalizerHelper.localized('field_required') : ""}
                    onChange={(e) => handleChange(field, e.target.value)}
                />
            </div>
        </div>
    );

    return (
        <Screen className="screen scroll delivery-details-screen">
            <RestaurantHeader className='full-width block' />
            <div className="content_container">
                <div className="container">
                    <div className="row">
                        <div className="col-md-12 user_questions">
                            {LocalizerHelper.localized('delivery_details')}
                        </div>
                    </div>
                    {fields.map(field => buildField(field, field))}
                    <div className="row form-row">
                        <div className="col-md-12">
                            <Button
                                className='accent-button full-width'
                                disabled={disableAction}
                                onClick={proceedWithOrder}
                            >
                                {LocalizerHelper.localized('confirm')}
                            </Button>
                        </div>
                    </div>
                </div>
            </div>
        </Screen>
    );
};

export default InfoDetails;
